import { PanelTitle } from 'components/layout/Panel'
import Panel from 'components/layout/Panel'
import { supportedCertifications } from 'types/profile'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Container, Text } from 'roadie-ui'
import { LocalizedText, useLocalization } from 'localization'
import type { ProfileDeliveryCertification } from 'data/driverCertifications'

const AchievementDisplay: React.FC<{
  length: number
  index: number
  image_url?: string
  name: string
  description: string
}> = (props) => {
  return (
    <Container.Grid
      key={props.index}
      $gridTemplateColumns="4.9rem auto"
      $padding="3rem"
      $gridGap="3rem"
      $borderColor="neutral-light"
      $borderStyle="solid"
      $borderWidth={props.index !== props.length - 1 ? '0 0 1px 0' : '0'}
    >
      <img src={props.image_url} alt={props.name} />
      <Container.Grid $gridGap="0.5rem">
        <Text.H3 $fontWeight="500" $fontSize="2.4rem" $color="neutral-black" $textAlign="left">
          {props.name}
        </Text.H3>
        <Text.Body2 $textAlign="left">{props.description}</Text.Body2>
      </Container.Grid>
    </Container.Grid>
  )
}

const MyAchievements: React.FC<{ badges: Array<any> }> = (props) => {
  const [activeTab, setActiveTab] = useState<'certifications' | 'badges'>('certifications')
  const { t } = useLocalization()
  const trainingCertifications = useSelector<any, ProfileDeliveryCertification[]>(
    (state) => state.trainingCertifications?.delivery_certifications_drivers ?? []
  )

  const certificationsToDisplay = trainingCertifications
    .filter((certification) => supportedCertifications.hasOwnProperty(certification.name))
    .filter((certification) => !certification.is_revoked)
    .map((certification) => ({
      name: certification.name,
      display_name: supportedCertifications[certification.name].display_name,
      description: t(supportedCertifications[certification.name].description_key),
      image_url: supportedCertifications[certification.name].url,
    }))

  return (
    <>
      <PanelTitle>
        <LocalizedText translationKey="dweb_dashboard_my_achievements" />
      </PanelTitle>
      <Panel noPadding>
        <Container.Grid
          $gridTemplateColumns="1fr 1fr"
          $justifyItems="center"
          $borderStyle="solid"
          $borderWidth="0 0 1px 0"
          $borderColor="neutral-light"
        >
          <Container.GridItem
            $width="100%"
            $borderStyle="solid"
            $borderWidth={activeTab === 'certifications' ? '0 0 2px 0' : '0'}
            $borderColor="primary-base"
          >
            <Button.Tertiary
              onClick={() => setActiveTab('certifications')}
              $width="100%"
              $padding="1.5rem 4rem"
              $color={activeTab === 'badges' ? 'primary-base' : 'neutral-base'}
              $backgroundColor="neutral-white"
            >
              <LocalizedText translationKey="dweb_dashboard_my_achievements_certifications" />
            </Button.Tertiary>
          </Container.GridItem>

          <Container.GridItem
            $width="100%"
            $borderStyle="solid"
            $borderWidth={activeTab === 'badges' ? '0 0 2px 0' : '0'}
            $borderColor="primary-base"
          >
            <Button.Tertiary
              onClick={() => setActiveTab('badges')}
              $width="100%"
              $padding="1.5rem 4rem"
              $color={activeTab === 'badges' ? 'primary-base' : 'neutral-base'}
              $backgroundColor="neutral-white"
            >
              <LocalizedText translationKey="dweb_dashboard_my_achievements_badges" />
            </Button.Tertiary>
          </Container.GridItem>
        </Container.Grid>
        <div>
          {activeTab === 'certifications' && (
            <Container.Grid>
              {certificationsToDisplay.length ? (
                certificationsToDisplay.map((certification, index) => (
                  <AchievementDisplay
                    key={index}
                    length={certificationsToDisplay.length}
                    index={index}
                    image_url={certification.image_url}
                    name={certification.display_name}
                    description={certification.description}
                  />
                ))
              ) : (
                <Container.GridItem $padding="1rem">
                  <Text.Body1 $fontWeight="300" $fontSize="1.6rem">
                    <LocalizedText translationKey="dweb_dashboard_my_achievements_no_certifications_earned_yet_drive_more_gigs_get" />
                  </Text.Body1>
                </Container.GridItem>
              )}
            </Container.Grid>
          )}
          {activeTab === 'badges' && (
            <Container.Grid>
              {props.badges.length ? (
                props.badges.map((badge, index) => (
                  <AchievementDisplay
                    key={index}
                    length={props.badges.length}
                    index={index}
                    image_url={badge.image_url}
                    name={badge.name}
                    description={badge.description}
                  />
                ))
              ) : (
                <Container.Base $padding="1rem">
                  <Text.Body1 $fontWeight="300" $fontSize="1.6rem">
                    <LocalizedText translationKey="dweb_dashboard_my_achievements_no_badges_earned_yet_drive_more_gigs_earn" />
                  </Text.Body1>
                </Container.Base>
              )}
            </Container.Grid>
          )}
        </div>
      </Panel>
    </>
  )
}

export default MyAchievements
